import type { Props as TourProps } from '@honeybook/hbui/dist/components/HBTour/HBTour';
import { action, observable } from 'mobx';
import type { StepTypes } from '~/components/Builder/Tour/HBTour.types';
import type { UiPersistenceStore } from '~/stores/ui-persistence.store';
import type { TTourStepTypes } from '~/types/tour.types';
import { logError } from '~/services/logger/logger.utils';
import { Omit } from 'lodash';

type StartTourProps = Omit<TourProps, 'runTour' | 'setRunTour'>;
export class TourStore {
    @observable isShow: boolean;
    @observable props: StartTourProps;
    @observable name: string;

    uiPersistenceStore: UiPersistenceStore;

    constructor({
        uiPersistenceStore
    }: {
        uiPersistenceStore: UiPersistenceStore;
    }) {
        this.reset();
        this.uiPersistenceStore = uiPersistenceStore;
    }

    @action.bound
    startTour(props: StartTourProps) {
        if (this.isShow) {
            logError('Trying to show a tour while another is already showing', {
                ongoing_tour_name: this.name,
                new_tour_name: props.name
            });
        }
        setTimeout(() => {
            this.props = props;
            this.name = this.props.name;
            this.setShowTour(true);
        });
    }

    @action.bound
    setShowTour(isShow: boolean) {
        this.isShow = isShow;
    }

    @action.bound
    reset() {
        this.isShow = false;
        this.props = null;
        this.name = null;
    }

    isTourOn(name: string) {
        return this.isShow && this.name === name;
    }

    private getTourInnerKey(tourKey: TTourStepTypes) {
        return `tourVisits.${tourKey}` as const;
    }

    getTourVisitsByKey({ tourKey }: { tourKey: TTourStepTypes }) {
        return this.uiPersistenceStore.get(this.getTourInnerKey(tourKey), []);
    }

    NextStepAction(joyrideProps) {
        const {
            primaryProps: { onClick: onNextClick }
        } = joyrideProps;

        return onNextClick;
    }

    hasStep({
        tourKey,
        stepName
    }: {
        tourKey: TTourStepTypes;
        stepName?: StepTypes;
    }) {
        const results = this.getTourVisitsByKey({ tourKey });

        if (stepName) {
            return results.includes(stepName);
        }

        return results.length > 0;
    }

    @action.bound
    setTourVisitStep({
        tourKey,
        stepName
    }: {
        tourKey: TTourStepTypes;
        stepName: StepTypes;
    }) {
        setTimeout(() => {
            const tourVisits = this.getTourVisitsByKey({ tourKey });

            if (!tourVisits.includes(stepName)) {
                this.uiPersistenceStore.set(this.getTourInnerKey(tourKey), [
                    ...tourVisits,
                    stepName
                ]);
            }
        });
    }
}
